import React, { useEffect, useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";

const HEATHROW_OPTIONS = [
  {
    label: "Heathrow Terminal 2",
    formatted_address: "Heathrow Terminal 2, Hounslow TW6 1EW, UK",
  },
  {
    label: "Heathrow Terminal 3",
    formatted_address: "Heathrow Terminal 3, Hounslow TW6 1QG, UK",
  },
  {
    label: "Heathrow Terminal 4",
    formatted_address: "Heathrow Terminal 4, Hounslow TW6 3XA, UK",
  },
  {
    label: "Heathrow Terminal 5",
    formatted_address: "Heathrow Terminal 5, Hounslow TW6 2GA, UK",
  },
  {
    label: "Heathrow Airport — terminal not known",
    formatted_address: "Heathrow Airport",
  },
];

const hasHeathrowIntent = (value = "") => /\b(?:HEATHROW|LHR)\b/i.test(String(value));

const normalizeRestrictions = (restrictions) => {
  const country = restrictions?.country;
  if (!country) return { country: ["gb"] };

  const normalizeCountry = (value) => {
    const code = String(value || "").toLowerCase();
    return code === "uk" ? "gb" : code;
  };

  return {
    ...restrictions,
    country: Array.isArray(country) ? country.map(normalizeCountry) : normalizeCountry(country),
  };
};

const PostcodeInput = ({
  placeholder,
  value,
  onChange,
  onPlaceSelected,
  error,
  className = "",
  style = null,
  inputProps = {},
  restrictions = { country: ["gb"] },
}) => {
  const ref = useRef();
  const inputElementRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);
  const useAutocomplete = process.env.REACT_APP_USE_AUTOCOMPLETE === "true";
  const normalizedRestrictions = normalizeRestrictions(restrictions);
  const showHeathrowOptions = inputFocused && hasHeathrowIntent(value);

  useEffect(() => {
    if (!showHeathrowOptions || typeof document === "undefined") return undefined;
    document.body.classList.add("ccg-heathrow-options-open");
    return () => document.body.classList.remove("ccg-heathrow-options-open");
  }, [showHeathrowOptions]);

  // ✅ Local test routes for mock autocomplete (no Google API)
  const MOCK_ADDRESSES = {
    "SL1 2LT": "Baxter Cl, Slough SL1 2LT, UK",
    "SL1 2LL": "Chalvey Rd E, Slough SL1 2LL, UK",
    "SL1 5JS": "Montem Ln, Slough SL1 5JS, UK",
  };

  const handleManualChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // 🧪 If input matches one of our mock codes, auto-fill a fake place
    const upper = inputValue.trim().toUpperCase();
    if (MOCK_ADDRESSES[upper]) {
      // simulate Google Autocomplete behavior
      const fakePlace = { formatted_address: MOCK_ADDRESSES[upper] };
      onPlaceSelected(fakePlace);
    }
  };

  const handlePlaceChanged = () => {
    if (!ref.current || typeof ref.current.getPlace !== "function") return;
    const place = ref.current.getPlace();
    if (place && place.formatted_address) {
      onPlaceSelected(place);
    }
  };

  const selectHeathrow = (option) => {
    onChange(option.formatted_address);
    onPlaceSelected({
      name: option.label,
      formatted_address: option.formatted_address,
      types: ["airport"],
    });
    // The option uses pointer-down prevention so it can be selected reliably.
    // Explicitly blur afterwards so Google's own Autocomplete panel also closes.
    if (inputElementRef.current) inputElementRef.current.blur();
    else if (typeof document !== "undefined") document.activeElement?.blur?.();
  };

  const suppliedOnFocus = inputProps.onFocus;
  const suppliedOnBlur = inputProps.onBlur;
  const mergedInputProps = {
    ...inputProps,
    onFocus: (event) => {
      setInputFocused(true);
      suppliedOnFocus?.(event);
    },
    onBlur: (event) => {
      // Let a pointer-down on a terminal option complete before hiding it.
      window.setTimeout(() => setInputFocused(false), 120);
      suppliedOnBlur?.(event);
    },
  };

  const inputEl = (
    <input
      ref={inputElementRef}
      type="text"
      value={value}
      onChange={handleManualChange}
      placeholder={placeholder}
      className={className || undefined}
      style={{
        width: "100%",
        padding: "0.6rem 0.75rem",
        marginBottom: "1rem",
        fontSize: "1rem",
        lineHeight: "1.5rem",
        height: "44px",
        borderRadius: "6px",
        border: error ? "1px solid #d9534f" : "1px solid #ccc",
        boxSizing: "border-box",
        ...(style || {}),
      }}
      {...mergedInputProps}
    />
  );

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <style>{`
        body.ccg-heathrow-options-open .pac-container {
          display: none !important;
        }
      `}</style>
      {useAutocomplete ? (
        <Autocomplete
          onLoad={(r) => (ref.current = r)}
          onPlaceChanged={handlePlaceChanged}
          restrictions={normalizedRestrictions}
          options={{
            types: ["geocode", "establishment"],
            fields: ["formatted_address", "geometry", "name", "place_id", "types"],
          }}
        >
          {inputEl}
        </Autocomplete>
      ) : inputEl}

      {showHeathrowOptions && (
        <div
          role="listbox"
          aria-label="Heathrow terminals"
          style={{
            position: "absolute",
            zIndex: 1001,
            top: "calc(100% + 2px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.14)",
            overflow: "hidden",
          }}
        >
          {HEATHROW_OPTIONS.map((option) => (
            <button
              key={option.label}
              type="button"
              role="option"
              aria-selected="false"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectHeathrow(option)}
              style={{
                display: "block",
                width: "100%",
                padding: "10px 12px",
                border: 0,
                borderBottom: "1px solid #f1f5f9",
                background: "#fff",
                color: "#111827",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "0.95rem",
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PostcodeInput;
